import React, { useContext, useEffect, useState } from "react";
import {
  IonButton,
  IonCol,
  IonContent,
  IonGrid,
  IonListHeader,
  IonRow,
  IonText,
  IonToolbar,
} from "@ionic/react";
import InfiniteScroll from "react-infinite-scroll-component";
import { withOmaUserService, WithOmaUserServiceProps } from "service";
import EsperiSpinner, { SpinnerSize } from "components/EsperiSpinner";
import ErrorView from "components/ErrorView";
import EmptyView, { EmptyViewType } from "components/EmptyView";
import { RouteComponentProps, withRouter } from "react-router";
import { LocalStorageKeys } from "util/Constants";
import { UserContext } from "providers/User";
import { useTranslation } from "react-i18next";
import ErrorViewSmall from "components/ErrorViewSmall";
import LoadMoreView from "components/LoadMoreView";
import { UIContext } from "../../../providers/UI/UIAppState";
import { OmaUserContext } from "../../../providers/OmaUser";
import { OmaUser } from "../../../providers/OmaUser/OmaUserActionTypes";
import OmaUserCell from "./OmaUserCell";
import ActiveHomePicker from "../../../components/ActiveHomePicker";
import SearchMenu from "components/SearchMenu";

interface OmaUsersProps extends WithOmaUserServiceProps, RouteComponentProps {
  hidden: boolean;
}

const OmaUsers = (props: OmaUsersProps) => {
  const { state } = useContext(UserContext);
  const { state: uiState } = useContext(UIContext);
  const { state: omaUserState } = useContext(OmaUserContext);

  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const [nextPage, setNextPage] = useState<string>("0");
  const [hasMoreItems, setHasMoreItems] = useState<boolean>(false);
  const [isFetchMoreError, setFetchMoreError] = useState(false);
  const [query, setQuery] = useState<string | undefined>(undefined);
  const [activeOnly, setActiveOnly] = useState<boolean>(true);

  const { t } = useTranslation();

  useEffect(() => {
    fetchOmaUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.activeHome, query, activeOnly]);

  function fetchOmaUsers() {
    setLoading(true);
    setError(false);
    props.omaUserService
      .fetchOmaUsers("0", uiState.activeHome, activeOnly, query, undefined)
      .then((response) => {
        setLoading(false);
        setNextPage(response.nextPage);
        setHasMoreItems(response.nextPage !== null);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
        setHasMoreItems(false);
      });
  }

  const fetchMore = () => {
    setFetchMoreError(false);
    setHasMoreItems(false);
    props.omaUserService
      .fetchOmaUsers(nextPage, uiState.activeHome, activeOnly, query, undefined)
      .then((response) => {
        setNextPage(response.nextPage);
        setHasMoreItems(response.nextPage !== null);
      })
      .catch(() => {
        setFetchMoreError(true);
        setHasMoreItems(false);
      });
  };

  useEffect(() => {
    const hidden =
      isLoading ||
      isError ||
      localStorage.getItem(LocalStorageKeys.SeenOnboarding) !== null ||
      state.firstName.length === 0;
  }, [state.firstName, isLoading, isError]);

  function renderFetchMoreError() {
    if (isFetchMoreError) {
      return (
        <ErrorViewSmall
          title={t("adminUsersLoadingMoreError")}
          onTryAgain={() => {
            setFetchMoreError(false);
            setHasMoreItems(true);
          }}
        />
      );
    }
  }

  const isEmpty = !isLoading && omaUserState.omaUsers.length === 0;
  return (
    <>
      <IonContent
        className="contentLightGrey contentMaxWidth"
        hidden={props.hidden}
      >
        <div id="contentId" className="feed">
          <EsperiSpinner size={SpinnerSize.Large} hidden={!isLoading} />
          <ErrorView
            hidden={!isError}
            onTryAgain={() => {
              fetchOmaUsers();
            }}
          />
          <InfiniteScroll
            scrollableTarget="contentId"
            dataLength={omaUserState.omaUsers.length}
            next={fetchMore}
            hasMore={hasMoreItems}
            loader={""}
          >
            <IonGrid className="feedList ion-padding">
              <IonListHeader className="itemListHeader">
                <IonToolbar className="headerToolbar">
                  <IonButton
                    className="buttonAddNew"
                    onClick={() => {
                      props.history.push("/omainen/uusi");
                    }}
                  >
                    {t("createNewOmaUser")}
                  </IonButton>
                  <SearchMenu
                    onQueryChange={(q: string | undefined) => setQuery(q)}
                    onActiveOnlyChange={(activeOnly: boolean) =>
                      setActiveOnly(activeOnly)
                    }
                    isLoading={isLoading}
                  />
                </IonToolbar>
              </IonListHeader>
              <EmptyView hidden={!isEmpty} type={EmptyViewType.OmaUsers} />
              <IonRow hidden={isEmpty} style={{ background: "white" }}>
                <IonCol
                  size="4"
                  className="ion-no-padding ion-padding-top ion-padding-start ion-padding-end"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <IonText className="body1Bold black ion-padding-bottom">
                    {t("omaUser")}
                  </IonText>
                </IonCol>
                <IonCol
                  size="4"
                  className="ion-no-padding ion-padding-top ion-padding-start ion-padding-end"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <IonText className="body1Bold black ion-padding-bottom">
                    {t("customers")}
                  </IonText>
                </IonCol>
                <IonCol
                  size="4"
                  className="ion-no-padding ion-padding-top ion-padding-start ion-padding-end"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <IonText className="body1Bold black ion-padding-bottom">
                    {t("status")}
                  </IonText>
                </IonCol>
              </IonRow>
              {omaUserState.omaUsers.map((item: OmaUser) => {
                return (
                  <OmaUserCell
                    key={item.id}
                    omaUser={item}
                    onClick={() => {
                      props.history.push("/omainen/muokkaa/" + item.id);
                    }}
                  />
                );
              })}
              {renderFetchMoreError()}
            </IonGrid>
          </InfiniteScroll>
        </div>
      </IonContent>
    </>
  );
};

export default withRouter(withOmaUserService(OmaUsers));
